/**
 * Inactivity-logout timing configuration. Production defaults — 50-minute
 * warning, 60-minute hard logout (decision L1) — are baked in; both can be
 * overridden at build time via `VITE_INACTIVITY_WARNING_MS` /
 * `VITE_INACTIVITY_LOGOUT_MS`. Shrink them to seconds in `.env.local` for
 * manual verification, or set them as GitHub Actions vars per environment.
 *
 * React-free on purpose: imported by both the in-tab hook
 * (`useInactivityLogout`) and the boot module (`initializeApp`) so the
 * warning/logout thresholds have a single source of truth.
 *
 * @agentic connections
 */

/** Default time-to-warning: 50 minutes (L1). @agentic connections */
const DEFAULT_WARNING_AT_MS = 50 * 60 * 1000;

/** Default time-to-logout: 60 minutes (L1). @agentic connections */
const DEFAULT_LOGOUT_AT_MS = 60 * 60 * 1000;

/**
 * Parse a positive-integer millisecond override, falling back to the
 * default when the value is unset, empty, or invalid.
 * @agentic connections
 */
const parseDurationMs = (
  raw: string | undefined,
  fallbackMs: number,
): number => {
  if (raw === undefined || raw === "") return fallbackMs;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : fallbackMs;
};

/** Time-to-warning in ms. @agentic connections */
export const WARNING_AT_MS = parseDurationMs(
  import.meta.env.VITE_INACTIVITY_WARNING_MS,
  DEFAULT_WARNING_AT_MS,
);

/** Time-to-logout in ms. @agentic connections */
export const LOGOUT_AT_MS = parseDurationMs(
  import.meta.env.VITE_INACTIVITY_LOGOUT_MS,
  DEFAULT_LOGOUT_AT_MS,
);
